var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
import { State, Action, Selector } from '@ngxs/store';
import { WidgetStateModel } from './widget.state.model';
import { ResetWidget, LogWidgetInteraction, LogWidgetInteractionFailure, LogWidgetInteractionSuccess, AuthWidget, AuthWidgetSuccess, AuthWidgetFailure, SetupWidget, SetInputEnabled } from './widget.actions';
import { WidgetAnalyticsService } from 'src/app/widget-auth/widget-analytics.service';
import { WidgetAuthService } from 'src/app/widget-auth/widget-auth.service';
import { finalize } from 'rxjs/operators';
import { UserAnalyticModel } from '../models/user-analytic.model';
import { DeveloperAnalyticModel } from '../models/developer-analytic.model';
import { AnalyticActivityModel } from '../models/analytic-activity.model';
import { WidgetUserTypeEnum } from '../models/widget-user-type.enum';
import { WidgetPlatformEnum } from '../models/widget-platform.enum';
import { WidgetInteractionEnum } from '../models/widget-interaction.enum';
import { plainToClass } from 'class-transformer';
const initialState = {
    // auth state
    userType: WidgetUserTypeEnum.User,
    authenticating: false,
    authenticated: false,
    // widget config
    widgetConfig: {
        id: '',
        type: ''
    },
    // auth data
    parentUrl: {
        full: '',
        domain: ''
    },
    embedded: false,
    // engine params
    engineVersion: '',
    engineAPIVersion: '',
    inputEnabled: true
};
let WidgetState = class WidgetState {
    constructor(authService, analyticsService) {
        this.authService = authService;
        this.analyticsService = analyticsService;
    }
    static getUserType(state) {
        return state.userType;
    }
    static getWidgetAuthenticating(state) {
        return state.authenticating;
    }
    static getWidgetAuthenticated(state) {
        return state.authenticated;
    }
    static getInputEnabled(state) {
        return state.inputEnabled;
    }
    static getEmbedded(state) {
        return state.embedded;
    }
    static getParentURL(state) {
        return state.parentUrl.full;
    }
    setupWidget(context, { hostingURL, embedded, widgetConfig }) {
        // extract host + port (optional) from url, match: 'protocol://host.name:port/path' -> 'host.name:port'
        const match = hostingURL.match(/:\/\/(.[^/]+)/);
        const domain = (match && match[1]) ? match[1] : hostingURL;
        context.patchState({
            // user type
            userType: widgetConfig.uaid ? WidgetUserTypeEnum.User : WidgetUserTypeEnum.Developer,
            // embedded state
            embedded: embedded,
            // config
            widgetConfig: widgetConfig,
            // hosting
            parentUrl: {
                full: hostingURL,
                domain: domain
            }
        });
    }
    authWidget(context, action) {
        const widgetState = context.getState();
        const { userType, widgetConfig } = widgetState;
        const authRequest$ = userType === WidgetUserTypeEnum.User
            // user auth: via uaid
            ? this.authService.authUser(widgetConfig.uaid)
            // developer auth: via developer key
            : this.authService.authDeveloper(this.buildDeveloperAnalytic(widgetState, WidgetInteractionEnum.Init, WidgetPlatformEnum.WidgetAPI) // build developer auth
            );
        // begin auth
        context.patchState({ authenticating: true });
        // check auth
        authRequest$
            .pipe(finalize(() => context.patchState({ authenticating: false })))
            .subscribe((response) => {
            const success = response.success;
            context.patchState({ authenticated: success });
            context.dispatch(success ? new AuthWidgetSuccess(userType, response) : new AuthWidgetFailure(userType, response));
        });
    }
    logWidgetInteraction(context, { interaction, source }) {
        const widgetState = context.getState();
        const userType = widgetState.userType;
        const analytic = userType === WidgetUserTypeEnum.User
            ? this.buildUserAnalytic(widgetState, interaction, source)
            : this.buildDeveloperAnalytic(widgetState, interaction);
        // log interaction
        this.analyticsService.sendAnalytic(analytic)
            .subscribe((response) => {
            if (response.success) {
                context.dispatch(new LogWidgetInteractionSuccess(interaction, response));
            }
            else {
                context.dispatch(new LogWidgetInteractionFailure(interaction, response));
            }
        });
    }
    resetWidget(context, action) {
        context.setState(initialState);
    }
    buildAppActivityData(widgetState) {
        const { engineVersion, engineAPIVersion, widgetConfig } = widgetState;
        // require activity data
        const baseActivityData = {
            engine_version: engineVersion,
            api_version: engineAPIVersion
        };
        // include widget params
        Object.entries(widgetConfig)
            // exclude developer key
            .filter(([key]) => key !== 'dk')
            // set url param: i.e., ?ui-info=true -> { ... 'url_param_ui-info': 'true' }
            .forEach(([key, value]) => {
            baseActivityData[`url_param_${key}`] = value;
        });
        return plainToClass(AnalyticActivityModel, baseActivityData);
    }
    setInputEnabled(context, action) {
        context.patchState({
            inputEnabled: action.enabled
        });
    }
    buildUserAnalytic(widgetState, activity, source) {
        const { widgetConfig, parentUrl } = widgetState;
        const uaid = widgetConfig.uaid;
        const domain = parentUrl.domain;
        const activityData = Object.assign({}, this.buildAppActivityData(widgetState), { 
            // include user keys + host
            parent_analytic_identifier: uaid, referer_domain: domain, 
            // activity source
            [`${activity}_source`]: source });
        const analytic = {
            analytic_platform: WidgetPlatformEnum.Widget,
            analytic_type: activity,
            analytic_data: activityData
        };
        return plainToClass(UserAnalyticModel, analytic);
    }
    buildDeveloperAnalytic(widgetState, activity, platform = WidgetPlatformEnum.Widget) {
        const { widgetConfig, parentUrl, embedded } = widgetState;
        const activityData = this.buildAppActivityData(widgetState);
        const analytic = {
            platform: platform,
            app_activity: activity,
            // app keys
            app_productkey: widgetConfig['app-productkey'],
            app_userid: widgetConfig['app-userid'],
            // user keys
            app_apikey: widgetConfig['dk'],
            app_domain: parentUrl.domain,
            app_activity_data: activityData,
            widget_is_fullscreen: !embedded
        };
        return plainToClass(DeveloperAnalyticModel, analytic);
    }
    ngxsOnInit(context) {
        // set fixed state
        context.patchState({
            engineVersion: Human.VERSION,
            engineAPIVersion: Human.API_VERSION
        });
    }
};
__decorate([
    Action(SetupWidget),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object, SetupWidget]),
    __metadata("design:returntype", void 0)
], WidgetState.prototype, "setupWidget", null);
__decorate([
    Action(AuthWidget),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object, AuthWidget]),
    __metadata("design:returntype", void 0)
], WidgetState.prototype, "authWidget", null);
__decorate([
    Action(LogWidgetInteraction),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object, LogWidgetInteraction]),
    __metadata("design:returntype", void 0)
], WidgetState.prototype, "logWidgetInteraction", null);
__decorate([
    Action(ResetWidget),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object, ResetWidget]),
    __metadata("design:returntype", void 0)
], WidgetState.prototype, "resetWidget", null);
__decorate([
    Action(SetInputEnabled),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object, SetInputEnabled]),
    __metadata("design:returntype", void 0)
], WidgetState.prototype, "setInputEnabled", null);
__decorate([
    Selector(),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [WidgetStateModel]),
    __metadata("design:returntype", void 0)
], WidgetState, "getUserType", null);
__decorate([
    Selector(),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [WidgetStateModel]),
    __metadata("design:returntype", void 0)
], WidgetState, "getWidgetAuthenticating", null);
__decorate([
    Selector(),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [WidgetStateModel]),
    __metadata("design:returntype", void 0)
], WidgetState, "getWidgetAuthenticated", null);
__decorate([
    Selector(),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [WidgetStateModel]),
    __metadata("design:returntype", void 0)
], WidgetState, "getInputEnabled", null);
__decorate([
    Selector(),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [WidgetStateModel]),
    __metadata("design:returntype", void 0)
], WidgetState, "getEmbedded", null);
__decorate([
    Selector(),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [WidgetStateModel]),
    __metadata("design:returntype", void 0)
], WidgetState, "getParentURL", null);
WidgetState = __decorate([
    State({
        name: 'widget',
        defaults: initialState
    }),
    __metadata("design:paramtypes", [WidgetAuthService,
        WidgetAnalyticsService])
], WidgetState);
export { WidgetState };
